export class SaveService {
    constructor(_registry, _provider, 
    /**
     * Told about each module that could not be loaded. Optional — a caller that
     * passes nothing gets the same degradation, silently.
     */
    _onLoadFailure) {
        this._registry = _registry;
        this._provider = _provider;
        this._onLoadFailure = _onLoadFailure;
    }
    /**
     * Hydrate every registered module, and **never reject**.
     *
     * ## Why this cannot be `Promise.all` over throwing reads
     *
     * It was, until 2026-08-14. `IDataProvider.get()` rejects on a backend error —
     * `SupabaseDataProvider` does — so a single failed read rejected the whole
     * `Promise.all`, which rejected `composeServices()`, whose only caller did not
     * catch. `PlatformServicesProvider` then sat on `services === null` and
     * rendered `null` forever: a blank page, permanently, with no error boundary
     * to catch it (a rejected promise is not a render throw) and no way out but a
     * reload.
     *
     * Measured by forcing exactly ONE of the three save reads to 401 while auth
     * and the other two succeeded: `visibleText: ""`, and it stayed that way. In
     * production the trigger was a transient Supabase `JWT issued at future` — a
     * clock skew on the backend — but any network blip does the same, so the
     * blast radius was "the app does not start" for a fault that lasts a second.
     *
     * ## What a failed read degrades to
     *
     * The module keeps whatever it already had, which for a fresh boot is its own
     * default — exactly the state of a player who has no save yet. That is a state
     * every module already handles, on every first launch, so this adds no new
     * case to reason about. `module.load()` still runs, so the in-memory view is
     * populated either way.
     *
     * The cost is honest and worth stating: a player whose read failed sees
     * default state for this session, and anything they then save overwrites the
     * server copy they could not read. That is a worse outcome than a retry, and a
     * much better one than a blank screen — the failure is reported through
     * `_onLoadFailure` so a caller can retry or warn, rather than being decided
     * here.
     */
    async loadAll() {
        // Still concurrent: each module's key is independent, so this stays one
        // round-trip rather than N. `allSettled` is the only change in shape — a
        // rejection now lands in a result instead of unwinding the whole call.
        const modules = this._registry.getAll();
        const results = await Promise.allSettled(modules.map(async (module) => {
            const raw = await this._provider.get(module.getKey());
            if (raw !== null) {
                const fromVersion = typeof raw['version'] === 'number' ? raw['version'] : 0;
                if (fromVersion < module.version) {
                    // A real migration happened — persist the upgraded shape.
                    module.save(module.migrate(raw, fromVersion));
                }
                else if (module.hydrate) {
                    // No migration needed — just cache in memory, don't re-write unchanged data
                    // (some modules' keys, e.g. the wallet's, are server-write-only by design
                    // and would fail every time otherwise).
                    module.hydrate(raw);
                }
                else {
                    module.save(raw);
                }
            }
        }));
        results.forEach((result, i) => {
            if (result.status === 'rejected') {
                this._onLoadFailure?.({ key: modules[i].getKey(), error: result.reason });
            }
            // Outside the branch on purpose: `load()` is what populates the module's
            // in-memory view, and a module whose READ failed needs it most — that is
            // the call that leaves it holding its default instead of nothing.
            modules[i].load();
        });
    }
}
